import { useState, type FormEvent } from 'react'
import { apiPost } from '../lib/api.ts'
import { useClearSession, useRefreshSession } from '../lib/session.ts'
import { AuthLayout, FormError, buttonClass, inputClass } from './AuthLayout.tsx'
import { WebauthnCancelled, presentSecurityKey, webauthnAvailable } from './webauthn.ts'

/**
 * The challenge for a user who already has a factor enrolled.
 *
 * Any enrolled factor satisfies it (§6c), so all three routes in are offered
 * side by side rather than one being the "real" way and the others buried. A
 * recovery code is last and named as a fallback, because using one costs a code
 * and means something has gone wrong.
 */
type Mode = 'totp' | 'recovery'

export function MfaScreen({ email }: { email: string }) {
  const [mode, setMode] = useState<Mode>('totp')
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)
  const refresh = useRefreshSession()
  const clear = useClearSession()

  async function submitTotp(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await apiPost('/auth/mfa', { code })
      await refresh()
    } catch {
      setError('That code was not accepted. Codes change every 30 seconds.')
      setBusy(false)
    }
  }

  async function submitRecovery(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await apiPost<{ codes_remaining: number }>('/auth/mfa/recovery', {
        code: recovery.trim(),
      })
      // Shown before navigating away: someone down to their last code needs to
      // know now, not the next time they cannot get in.
      setRemaining(result.codes_remaining)
      await refresh()
    } catch {
      setError('That recovery code was not accepted. Each one works only once.')
      setBusy(false)
    }
  }

  async function useKey() {
    setBusy(true)
    setError(null)
    try {
      await presentSecurityKey()
      await refresh()
    } catch (caught) {
      setError(
        caught instanceof WebauthnCancelled
          ? null
          : 'That key was not accepted. Try again, or use a code.',
      )
      setBusy(false)
    }
  }

  async function cancel() {
    await apiPost('/auth/logout')
    clear()
    await refresh()
  }

  return (
    <AuthLayout title="Two-factor code" subtitle={`Signed in as ${email}`}>
      {mode === 'totp' ? (
        <form onSubmit={submitTotp} className="space-y-3">
          <div>
            <label htmlFor="code" className="mb-1 block text-sm font-medium text-ink">
              6-digit code from your authenticator app
            </label>
            <input
              id="code"
              // one-time-code lets a password manager or the OS offer the code.
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`${inputClass} text-center font-mono text-lg tracking-[0.4em]`}
            />
          </div>

          <button type="submit" disabled={busy || code.length < 6} className={buttonClass}>
            {busy ? 'Checking…' : 'Verify'}
          </button>
        </form>
      ) : (
        <form onSubmit={submitRecovery} className="space-y-3">
          <div>
            <label htmlFor="recovery" className="mb-1 block text-sm font-medium text-ink">
              One of your recovery codes
            </label>
            <input
              id="recovery"
              autoComplete="one-time-code"
              required
              autoFocus
              placeholder="a1b2c-3d4e5"
              value={recovery}
              onChange={(e) => setRecovery(e.target.value)}
              className={`${inputClass} text-center font-mono`}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Using one spends it. Generate a new set from your security settings afterwards.
            </p>
          </div>

          <button type="submit" disabled={busy || recovery.trim().length < 8} className={buttonClass}>
            {busy ? 'Checking…' : 'Use recovery code'}
          </button>
        </form>
      )}

      <FormError message={error} />

      {remaining !== null ? (
        <p role="status" className="mt-3 text-sm text-ink">
          {remaining === 0
            ? 'That was your last recovery code. Generate a new set straight away.'
            : `${remaining} recovery code${remaining === 1 ? '' : 's'} left.`}
        </p>
      ) : null}

      <div className="mt-5 space-y-2 border-t border-line pt-4">
        {webauthnAvailable() ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void useKey()}
            className="w-full rounded-md border border-line px-3 py-2 text-sm font-semibold text-ink hover:border-brand-400 disabled:opacity-50"
          >
            Use a security key
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => {
            setError(null)
            setMode(mode === 'totp' ? 'recovery' : 'totp')
          }}
          className="w-full text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          {mode === 'totp' ? 'Lost your device? Use a recovery code' : 'Use your authenticator app'}
        </button>
      </div>

      <button
        type="button"
        onClick={cancel}
        className="mt-4 text-xs text-ink-muted underline underline-offset-2 hover:text-ink"
      >
        Sign in as someone else
      </button>
    </AuthLayout>
  )
}
